import { message, notification } from 'antd'
import moment from 'moment'
import htmlToDraft from 'html-to-draftjs'
import { EditorState, ContentState } from 'draft-js'

// THIS IS A GENERAL FUNCTION WHICH HANDLES SETTING FORMVALUES IN FORMDATA, BUT NO FILES INCLUDED.
// IT ASSUMES THAT ALL FILES ARE PRE-UPLOADED
function isPlainObject(v) {
  return Object.prototype.toString.call(v) === '[object Object]'
}

export function withItalianLocaleFallback(values, fields = ['title', 'description']) {
  const nextValues = { ...values }

  fields.forEach((field) => {
    const italianKey = `${field}_it`
    const italianValue = nextValues[italianKey]

    if (italianValue === '' || italianValue == null) return

    ;['ar', 'es'].forEach((locale) => {
      const localeKey = `${field}_${locale}`
      if (nextValues[localeKey] === '' || nextValues[localeKey] == null) {
        nextValues[localeKey] = italianValue
      }
    })
  })

  return nextValues
}

export function appendFormValues(formData, formValues) {
  const datePattern = /^(0[1-9]|[12][0-9]|3[01])\/(0[1-9]|1[012])\/(19|20)\d\d$/

  const append = (fd, key, value) => {
    if (value === null || value === undefined) return

    // Strings (with date normalization)
    if (typeof value === 'string') {
      fd.append(
        key,
        datePattern.test(value) ? value.replace(/\//g, '-') : value
      )
      return
    }

    // Numbers / booleans
    if (typeof value === 'number' || typeof value === 'boolean') {
      fd.append(key, String(value))
      return
    }

    // Date instance
    if (value instanceof Date) {
      fd.append(key, value.toISOString())
      return
    }

    // Arrays
    if (Array.isArray(value)) {
      value.forEach((item, idx) => {
        if (isPlainObject(item)) {
          // Array of objects -> key[0][prop]
          Object.entries(item).forEach(([k, v]) => {
            append(fd, `${key}[${idx}][${k}]`, v)
          })
        } else {
          // Array of primitives -> key[]
          append(fd, `${key}[]`, item)
        }
      })
      return
    }

    // Plain objects (nested)
    if (isPlainObject(value)) {
      Object.entries(value).forEach(([k, v]) => {
        append(fd, `${key}[${k}]`, v)
      })
      return
    }

    // Fallback
    fd.append(key, String(value))
  }

  Object.entries(formValues).forEach(([key, value]) =>
    append(formData, key, value)
  )
}

export const appendFormData = (profileFields, formData, values) => {
  const dateFields = ['birth_date', 'nid_expire_at', 'residence_expire_at']

  profileFields.forEach((field) => {
    if (values[field]) {
      // Check if the field is one of the specific date fields
      if (dateFields.includes(field)) {
        // Replace '/' with '-' in the date string
        const modifiedDate = values[field].replace(/\//g, '-')
        formData.set(field, modifiedDate)
      } else {
        // For other fields, set the value as it is
        formData.set(field, values[field])
      }
    }
  })
}

export const appendFiles = (formData, fieldName, files) => {
  files.forEach((file) => formData.append(fieldName, file))
}

// ANTD DATEPICKER VALIDATION
export function disabledDateBeforeToday(current) {
  return current && current < moment().startOf('day')
}

export function disabledDateAfterToday(current) {
  return current && current > moment().startOf('day')
}

// DEBOUNCE FUNCTION
export const debounce = (func, wait) => {
  let timeout

  return function executedFunction(...args) {
    const later = () => {
      clearTimeout(timeout)
      func(...args)
    }

    clearTimeout(timeout)
    timeout = setTimeout(later, wait)
  }
}

// THIS FUNCTION USED IF THE BACKEND RETURNED DATE IN THIS FORMAT 2027-02-11 00:00:00
export function convertDateFormat(datetimeString) {
  if (!datetimeString) return null

  // Extract the date part (before the space)
  const datePart = datetimeString.split(' ')[0]

  // Split the date into its components
  const [year, month, day] = datePart.split('-')

  // Reformat to MM/DD/YYYY
  const formattedDate = `${month}/${day}/${year}`

  return formattedDate
}

export const formatDate = (dateString) => {
  // Convert the date string "22-09-2024 21:04:17" into a Date object
  const [datePart, timePart] = dateString.split(' ')
  const [day, month, year] = datePart.split('-')
  const [hours, minutes, seconds] = timePart.split(':')
  const date = new Date(year, month - 1, day, hours, minutes, seconds)

  // Format the date into "12 Apr 2023"
  const formattedDate = new Intl.DateTimeFormat('en-GB', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  }).format(date)

  // Format the time into "5:00 PM"
  const formattedTime = new Intl.DateTimeFormat('en-US', {
    hour: 'numeric',
    minute: 'numeric',
    hour12: true,
  }).format(date)

  return { formattedDate, formattedTime }
}

// convert a date string like 2024-10-26 into European format (26/10/2024)
export const toEuropeanDateFormat = (dateString) => {
  const [year, month, day] = dateString.split('-')
  return `${day}/${month}/${year}`
}

// Handle ISO timestamps like 2024-08-31T15:25:58.000000Z and return both date and time in European format
export const toEuropeanDateTimeFormat = (isoString) => {
  const date = new Date(isoString)
  if (isNaN(date)) return 'Invalid date'

  const day = String(date.getDate()).padStart(2, '0')
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const year = date.getFullYear()

  const hours = String(date.getHours()).padStart(2, '0')
  const minutes = String(date.getMinutes()).padStart(2, '0')

  return `${day}/${month}/${year} ${hours}:${minutes}`
}

export const toServerDateFormat = (dateString) => {
  const [day, month, year] = dateString.split('-')
  return `${year}-${month}-${day}`
}

// Error Handling
export const errorHandling = (error) => {
  if (error.response) {
    // Check for message and errors object
    const message = error.response.data.message || 'Errore sconosciuto'
    const errors = error.response.data.errors

    if (errors) {
      // Map through each key in the errors object and take only the first error message
      const firstErrors = Object.keys(errors).reduce((acc, key) => {
        acc[key] = errors[key][0]
        return acc
      }, {})

      // Return both message and the modified errors object
      return { message, errors: firstErrors }
    }

    // If only message is available
    return { message }
  } else if (error.request) {
    // Error related to the request made but no response was received
    return { message: `Errore nella richiesta: ${error.message}` }
  } else {
    // Other kinds of errors (e.g., setup issues)
    return { message: error.message }
  }
}

export const copyToClipboard = (text) => {
  navigator.clipboard
    .writeText(text)
    .then(() => {
      // Handle successful copying here, like showing a notification
      message.success('Testo copiato negli appunti')
    })
    .catch((err) => {
      // Handle errors here
      message.error('Impossibile copiare il testo: ', err)
    })
}

const NOTIFICATION_TYPES = new Set(['success', 'error', 'info', 'warning'])

export const openNotification = (
  type,
  description,
  message,
  icon,
  onClick,
  style
) => {
  const normalizedType = NOTIFICATION_TYPES.has(type) ? type : 'info'

  notification.open({
    message,
    description,
    ...(icon ? { icon } : {}),
    type: normalizedType,
    onClick,
    style,
  })
}

export const initializeEditorState = (htmlString) => {
  const blocksFromHtml = htmlToDraft(htmlString)
  const { contentBlocks, entityMap } = blocksFromHtml
  const contentState = ContentState.createFromBlockArray(
    contentBlocks,
    entityMap
  )
  return EditorState.createWithContent(contentState)
}

export function formatFileSize(bytes) {
  const units = ['Bytes', 'KB', 'MB', 'GB', 'TB']

  // If the size is 0, return 0 Bytes
  if (bytes === 0) return '0 Bytes'

  const index = Math.floor(Math.log(bytes) / Math.log(1024))
  const size = bytes / Math.pow(1024, index)

  // Limiting to 2 decimal places for better readability
  return `${size.toFixed(2)} ${units[index]}`
}
